import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IntlContext } from "context";
import FieldGroup from "../Element/FieldGroup";
import Label from "../Element/Label";
import Adornment from "../Element/Adornment";
import Feedback from "../Element/Feedback";
import Input from "../Select/Input";
import Checkbox from "../Element/Checkbox";
import { useFormState } from "react-final-form";

export interface FormSelectCheckboxProps extends FormBaseCheckboxProps {
    inputProps: SelectProps;
}

const FormSelectCheckbox: React.FC<FormSelectCheckboxProps> = ({
    error,
    errorMessage,
    infoMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
    fieldControlProps: fieldControlPropsExtended,
    adornmentStart,
    adornmentEnd,
    listHeader,
    adormentRight,
    displaySelected = false,
}) => {
    const intl = useContext(IntlContext);
    const disabled = inputProps.disabled;
    const required = inputProps.required;
    const { values } = useFormState();
    const wrapperListRef = useRef(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const selectedItem = inputProps.options.find((item) => item.value === inputProps.value);

    const fieldControlProps = Object.assign(
        {
            className: "field-control field-control__select-wrapper",
            errorClassName: "error",
            disabledClassName: "disabled",
        },
        fieldControlPropsExtended
    );

    const openMenu = useCallback(() => {
        return setIsOpen(true);
    }, []);

    if (
        inputProps.title === undefined &&
        labelProps !== undefined &&
        labelProps.label !== undefined &&
        labelProps.label !== null
    ) {
        inputProps.title = labelProps.label.toString();
    }

    useEffect(() => {
        const hideWidget = () => {
            setIsOpen(false);
        };

        const handleClickOutside = (event: MouseEvent) => {
            if (!(wrapperListRef.current as any)?.contains(event.target)) {
                hideWidget();
            }
        };
        document.addEventListener("mousedown", handleClickOutside, true);
        return () => {
            hideWidget();
        };
    }, []);

    const handleOnChangeCheckbox = useCallback(
        (e: any) => {
            inputProps.onChange && inputProps.onChange(e);
        },
        [inputProps]
    );

    const displaySelectedElements = () => {
        if (!displaySelected || disabled) {
            return false;
        }
        if (!Array.isArray(inputProps?.value)) {
            return false;
        }
        if (inputProps?.value.length === 0) {
            return false;
        }
        return true;
    };

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label htmlFor={inputProps.id} disabled={disabled} error={error} required={required} {...labelProps}>
                    {labelProps.label}
                </Label>
            )}

            <div className="field-control-wrapper field-control-wrapper-stats" ref={wrapperListRef}>
                <div
                    className={`${fieldControlProps.className} ${disabled ? fieldControlProps.disabledClassName : ""} ${
                        error ? fieldControlProps.errorClassName : ""
                    }`}
                >
                    {adornmentStart !== undefined && <Adornment position="start">{adornmentStart}</Adornment>}
                    <div className={inputProps.className || "field-control__select" + (isOpen ? " is-open" : "")}>
                        <Input
                            {...{
                                id: inputProps.id,
                                //name: "select_checkbox_" + inputProps.name,
                                placeholder: selectedItem
                                    ? undefined
                                    : inputProps.placeholder ||
                                      intl.formatMessage("generic.form.select_custom.placeholder"),
                                autoFocus: inputProps.autofocus,
                                disabled: inputProps.disabled,
                                readOnly: inputProps.readOnly,
                                onFocus: openMenu,
                                onMouseDown: openMenu,
                                title: inputProps.title ?? "",
                                ...inputProps.searchInputProps,
                            }}
                        />
                    </div>

                    {adornmentEnd !== undefined && <Adornment position="end">{adornmentEnd}</Adornment>}

                    {listHeader && (
                        <div
                            className="field-control-select__list-select-all"
                            style={{ display: isOpen ? "initial" : "none" }}
                        >
                            {listHeader}
                        </div>
                    )}

                    <ul
                        className="field-control-select__list field-control-select__list-stats"
                        style={{ display: isOpen ? "initial" : "none" }}
                    >
                        {inputProps.options.map((item, index) => (
                            <li
                                key={index + "_" + item.value}
                                className="field-control-select__item field-control-select__item-stats"
                            >
                                <Checkbox
                                    id={inputProps.id + "_" + index}
                                    className={"field-control-checkbox__input field-control-checkbox__input-stats"}
                                    groupClassName={"field-control-checkbox__group field-control-checkbox__group-stats"}
                                    name={inputProps.name}
                                    options={[{ label: item.label, value: item.value }]}
                                    onChange={handleOnChangeCheckbox}
                                    fields={{
                                        value: Array.isArray(values?.[inputProps.name]) ? values[inputProps.name] : [],
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
                {adormentRight}
            </div>

            {displaySelectedElements() && (
                <Feedback className="field__feedback selected">
                    {intl.formatMessage(
                        typeof displaySelected === "boolean" ? "generic.selected_items" : displaySelected,
                        { items: Array.isArray(inputProps?.value) ? inputProps?.value.length : 0 }
                    )}
                </Feedback>
            )}
            {infoMessage !== undefined && !disabled && <Feedback className="field__feedback">{infoMessage}</Feedback>}
            {error && errorMessage !== undefined && (
                <Feedback className="field__feedback error">{errorMessage}</Feedback>
            )}
        </FieldGroup>
    );
};

export default FormSelectCheckbox;
